// src/pages/Dashboard/DashboardHome/components/MonthlyMetricsCards.tsx
import React, { useRef, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { DollarSign, TrendingUp, TrendingDown, Percent } from "lucide-react";
import { Card } from "@/components/Card";
import api from "@/utils/connector/ApiConnector";
import { useUser } from "@/utils/userContext/UserContext";
import ContentLoader from "react-content-loader";

interface MonthlyMetricsCardsProps {
  month: number;
  year: number;
}

interface CardInfo {
  title: string;
  value: string | number;
  change?: number | string | null;
  changeLabel?: string;
  icon: React.ReactNode;
  isPercentage?: boolean;
  colorClass?: string;
}

interface MonthlyMetrics {
  revenue: number;
  subscriptionsStarted: number;
  churnRate: number;
  revenueGrowth: number;
  previousMonthRevenue: number;
}

function buildDefaultMonthlyCards(): CardInfo[] {
  return [
    {
      title: "Receita Total",
      value: "R$ 0,00",
      changeLabel: "0 novas inscrições",
      icon: <DollarSign size={20} />,
    },
    {
      title: "Churn",
      value: "0,0%",
      changeLabel: "taxa de cancelamento",
      icon: <Percent size={20} />,
      isPercentage: true,
      colorClass: "value-positive",
    },
    {
      title: "Crescimento",
      value: "+0,0%",
      changeLabel: "vs. mês anterior",
      icon: <TrendingUp size={20} />,
      isPercentage: true,
      colorClass: "",
    },
  ];
}

function toNumberOrZero(v: any): number {
  if (v === null || v === undefined) return 0;
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

function transformMonthlyData(raw: any): CardInfo[] {
  const metrics: MonthlyMetrics = raw?.monthlyMetrics;
  if (!metrics) return buildDefaultMonthlyCards();

  const revenue = toNumberOrZero(metrics.revenue);
  const churnRate = toNumberOrZero(metrics.churnRate);
  const revenueGrowth = toNumberOrZero(metrics.revenueGrowth);
  const subscriptionsStarted = toNumberOrZero(metrics.subscriptionsStarted);

  return [
    {
      title: "Receita Total",
      value: `R$ ${revenue.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
      changeLabel: `${subscriptionsStarted} ${subscriptionsStarted === 1 ? 'nova inscrição' : 'novas inscrições'}`,
      icon: <DollarSign size={20} />,
    },
    {
      title: "Churn",
      value: `${churnRate.toFixed(1)}%`,
      changeLabel: "taxa de cancelamento",
      icon: <Percent size={20} />,
      isPercentage: true,
      colorClass: churnRate > 5 ? "value-negative" : churnRate > 2 ? "value-warning" : "value-positive",
    },
    {
      title: "Crescimento",
      value: `${revenueGrowth >= 0 ? '+' : ''}${revenueGrowth.toFixed(1)}%`,
      changeLabel: "vs. mês anterior",
      icon: revenueGrowth >= 0 ? <TrendingUp size={20} /> : <TrendingDown size={20} />,
      isPercentage: true,
      colorClass: revenueGrowth < 0 ? "value-negative" : revenueGrowth > 5 ? "value-positive" : "",
    },
  ];
}

async function fetchMonthlyData(coworkingId: string, token: string, month: number, year: number): Promise<CardInfo[]> {
  const path = `/coworking/${encodeURIComponent(coworkingId)}/dashboard?month=${month}&year=${year}`;
  const resp = await api.get(path, {
    headers: { Authorization: `Bearer ${token}` },
  });
  const raw = (resp && (resp as any).data) ? (resp as any).data : resp;
  return transformMonthlyData(raw);
}

const MonthlyMetricsCards: React.FC<MonthlyMetricsCardsProps> = ({ month, year }) => {
  const userContext = useUser();
  const userData: any = userContext?.userData;
  const token = userData?.auth;
  const coworkingId = userData?.coworkingId ?? null;
  
  const lastSuccessfulDataRef = useRef<CardInfo[]>([]);

  const { data: cardsInfo, isLoading, isFetching, error } = useQuery({
    queryKey: ['monthlyMetrics', coworkingId, month, year],
    queryFn: () => fetchMonthlyData(coworkingId!, token!, month, year),
    enabled: !!coworkingId && !!token,
    staleTime: 30000,
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    if (cardsInfo && cardsInfo.length > 0) {
      lastSuccessfulDataRef.current = cardsInfo;
    }
  }, [cardsInfo]);

  const displayData = (cardsInfo && cardsInfo.length > 0) ? cardsInfo : lastSuccessfulDataRef.current;
  const hasData = displayData.length > 0;
  const loading = isLoading && !hasData;
  const cardsToRender = hasData ? displayData : buildDefaultMonthlyCards();

  function listCards(cards: CardInfo[]) {
    return (
      <>
        {cards.map((card, index) => (
          <Card key={index} className="stat-card">
            <div className="card-header">
              <h3 className="card-title">{card.title}</h3>
              <div className="card-icon">{card.icon}</div>
            </div>
            <div className="card-content">
              <div className={`stat-value ${card.colorClass || ''}`}>{card.value}</div>
              {card.changeLabel && (
                <p className="stat-change stat-info">{card.changeLabel}</p>
              )}
            </div>
          </Card>
        ))}
      </>
    );
  }

  function listSkeletons() {
    return (
      <>
        {Array.from({ length: 3 }).map((_, idx) => (
          <Card key={idx} className="stat-card">
            <ContentLoader speed={2} width="100%" height={120} backgroundColor="#f3f3f3" foregroundColor="#ecebeb">
              <rect x="10" y="10" rx="4" ry="4" width="60%" height="20" />
              <circle cx="90%" cy="20" r="15" />
              <rect x="10" y="50" rx="4" ry="4" width="40%" height="28" />
              <rect x="10" y="90" rx="4" ry="4" width="70%" height="14" />
            </ContentLoader>
          </Card>
        ))}
      </>
    );
  }

  return (
    <div className={`stats-grid stats-grid-3 ${isFetching && !isLoading ? 'refetching' : ''}`}>
      {loading ? listSkeletons() : listCards(cardsToRender)}
    </div>
  );
};

export default MonthlyMetricsCards;
